import { format } from "date-fns";
import { useTranslate } from "ra-core";

import { APP_LOCALE } from "../misc/locale";
import type { DealActivityNextStepStatus } from "../types";

const DATE_TIME_LOCAL_FORMAT = "yyyy-MM-dd'T'HH:mm";

const NEXT_STEP_EXCEPTION_FALLBACKS: Record<string, string> = {
  no_contact: "Agrega un contacto a este lead para agendar el siguiente paso",
  attempts_exhausted:
    "Ya se alcanzó el máximo de intentos para el siguiente paso",
  next_activity_archived: "La siguiente actividad ya no está disponible",
};

/**
 * Almikia Epic EA (TASK-111), DATA CONTRACT EA (7): the proposed next step,
 * in the contract's own exception order -- "Sin siguiente paso" (`'none'`),
 * a short Spanish explanation for the 3 exceptions, or the editable
 * "Siguiente paso: {activity} · {date y hora}" preview (`'created'`), not
 * earlier than now (enforced server-side; the input itself only guides the
 * advisor).
 */
export function NextStepPreviewBlock({
  status,
  nextActivityName,
  dueAtLocal,
  onDueAtLocalChange,
}: {
  status: DealActivityNextStepStatus;
  nextActivityName?: string;
  dueAtLocal: string;
  onDueAtLocalChange: (value: string) => void;
}) {
  const translate = useTranslate();

  if (status === "none") {
    return (
      <p className="text-sm text-muted-foreground">
        {translate("resources.deals.activity_log.next_step.none", {
          _: "Sin siguiente paso",
        })}
      </p>
    );
  }

  if (status !== "created") {
    const key =
      status === "no_contact"
        ? "resources.deals.activity_log.next_step.no_contact"
        : status === "attempts_exhausted"
          ? "resources.deals.activity_log.next_step.attempts_exhausted"
          : "resources.deals.activity_log.next_step.next_activity_archived";
    return (
      <p className="text-sm text-muted-foreground">
        {translate(key, { _: NEXT_STEP_EXCEPTION_FALLBACKS[status] })}
      </p>
    );
  }

  const formattedDueAt = dueAtLocal
    ? new Intl.DateTimeFormat(APP_LOCALE, {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(dueAtLocal))
    : "";

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor="activity-log-next-due-at" className="text-sm font-medium">
        {translate("resources.deals.activity_log.next_step.label", {
          activity: nextActivityName ?? "",
          date: formattedDueAt,
          _: `Siguiente paso: ${nextActivityName ?? ""} · ${formattedDueAt}`,
        })}
      </label>
      <input
        id="activity-log-next-due-at"
        type="datetime-local"
        value={dueAtLocal}
        min={format(new Date(), DATE_TIME_LOCAL_FORMAT)}
        aria-label={translate(
          "resources.deals.activity_log.next_step.due_at_label",
          {
            _: "Fecha y hora del siguiente paso",
          },
        )}
        onChange={(e) => onDueAtLocalChange(e.target.value)}
        className="min-h-11 rounded-md border border-input px-3 py-2 text-[15px] tabular-nums"
      />
    </div>
  );
}
